import { createThemePreset } from '@/styles/theme/themeSchema'
import { marketDark, marketLight } from '@/styles/theme/presets/energyMarketColors'

// Congestion direction (PI-659): aliases of the palette, as in the profiteia preset.
const congestionColors = {
  feedIn: 'var(--color-palette-amber)',
  offtake: 'var(--color-palette-blue)',
};

const shared = {
  radius: {
    xs: '0.25rem',
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    pill: '9999px',
  },
  space: {
    1: '0.25rem',
    2: '0.5rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    controlXXs: '0.4375rem',
    controlXSm: '0.5rem',
    controlXMd: '0.75rem',
    controlXLg: '0.875rem',
    controlYXs: '0.1875rem',
    controlYSm: '0.3125rem',
    controlYMd: '0.5rem',
    controlYLg: '0.625rem',
    card: '0.875rem',
    section: '1.25rem',
    dialog: '1rem',
    page: '1.25rem',
  },
  size: {
    controlXs: '1.625rem',
    controlSm: '1.875rem',
    controlMd: '2.25rem',
    controlLg: '2.75rem',
    iconSm: '0.9375rem',
    iconMd: '1rem',
    iconLg: '1.125rem',
    header: '3.75rem',
    sidebar: '14rem',
  },
  font: {
    sans: 'IBM Plex Sans, Inter, ui-sans-serif, system-ui, sans-serif',
    display: 'IBM Plex Sans, Inter, ui-sans-serif, system-ui, sans-serif',
  },
}

const light = {
  color: {
    brand: {
      // Anthropic brand accent; https://github.com/anthropics/skills/tree/main/skills/brand-guidelines
      claude: '217 119 87',
      primary: '15 23 42',
      primaryHover: '2 6 23',
      primarySoft: '241 245 249',
      accent: '59 130 246',
      accentSoft: '239 246 255',
    },
    bg: {
      app: '250 250 249',
      subtle: '245 245 244',
      canvas: '255 255 255',
    },
    surface: {
      base: '255 255 255',
      raised: '250 250 249',
      elevated: '255 255 255',
      inverse: '15 23 42',
    },
    border: {
      subtle: '231 229 228',
      default: '214 211 209',
      strong: '168 162 158',
    },
    text: {
      primary: '28 25 23',
      secondary: '87 83 78',
      muted: '120 113 108',
      inverse: '255 255 255',
    },
    state: {
      success: '34 197 94',
      warning: '217 119 6',
      danger: '220 38 38',
      info: '37 99 235',
      focus: '15 23 42',
    },
    palette: {
      red: '220 38 38',
      rose: '225 29 72',
      orange: '234 88 12',
      amber: '217 119 6',
      yellow: '202 138 4',
      lime: '101 163 13',
      green: '34 197 94',
      emerald: '5 150 105',
      teal: '13 148 136',
      cyan: '8 145 178',
      sky: '2 132 199',
      blue: '37 99 235',
      indigo: '79 70 229',
      violet: '124 58 237',
      purple: '147 51 234',
      fuchsia: '192 38 211',
      pink: '219 39 119',
    },
    market: marketLight,
    congestion: congestionColors,
    auth: {
      brandStart: '17 24 39',
      brandMid: '17 24 39',
      brandEnd: '28 25 23',
      brandDivider: '147 197 253',
      formStart: '255 255 255',
      formMid: '255 255 255',
      formEnd: '255 255 255',
      formDivider: '214 211 209',
      formShadow: '28 25 23',
    },
    shadow: '28 25 23',
    overlay: {
      scrim: '28 25 23',
      brand: '15 23 42',
      accent: '37 99 235',
    },
    static: {
      black: '0 0 0',
      white: '255 255 255',
    },
  },
  shadow: {
    card: '0 6px 18px rgba(28, 25, 23, 0.06)',
    cockpitCard: '0 1px 2px rgba(28, 25, 23, 0.04), 0 8px 16px -8px rgba(28, 25, 23, 0.08)',
    elevated: '0 8px 22px rgba(28, 25, 23, 0.10)',
    dialog: '0 18px 48px rgba(28, 25, 23, 0.14)',
    focus: '0 0 0 4px rgba(15, 23, 42, 0.12)',
  },
  glow: {
    primary: '0 0 0 1px rgba(15, 23, 42, 0.14), 0 10px 26px rgba(15, 23, 42, 0.08)',
    accent: '0 0 0 1px rgba(37, 99, 235, 0.12), 0 10px 26px rgba(37, 99, 235, 0.08)',
  },
  ...shared,
}

const dark = {
  color: {
    brand: {
      // Anthropic brand accent; https://github.com/anthropics/skills/tree/main/skills/brand-guidelines
      claude: '217 119 87',
      primary: '214 211 209',
      primaryHover: '245 245 244',
      primarySoft: '41 37 36',
      accent: '147 197 253',
      accentSoft: '30 41 59',
    },
    bg: {
      app: '17 24 39',
      subtle: '28 25 23',
      canvas: '28 25 23',
    },
    surface: {
      base: '28 25 23',
      raised: '41 37 36',
      elevated: '68 64 60',
      inverse: '245 245 244',
    },
    border: {
      subtle: '68 64 60',
      default: '87 83 78',
      strong: '120 113 108',
    },
    text: {
      primary: '245 245 244',
      secondary: '214 211 209',
      muted: '168 162 158',
      inverse: '28 25 23',
    },
    state: {
      success: '74 222 128',
      warning: '251 191 36',
      danger: '252 165 165',
      info: '147 197 253',
      focus: '214 211 209',
    },
    palette: {
      red: '252 165 165',
      rose: '253 164 175',
      orange: '253 186 116',
      amber: '251 191 36',
      yellow: '253 224 71',
      lime: '190 242 100',
      green: '74 222 128',
      emerald: '52 211 153',
      teal: '45 212 191',
      cyan: '103 232 249',
      sky: '125 211 252',
      blue: '147 197 253',
      indigo: '165 180 252',
      violet: '196 181 253',
      purple: '216 180 254',
      fuchsia: '240 171 252',
      pink: '249 168 212',
    },
    market: marketDark,
    congestion: congestionColors,
    auth: {
      brandStart: '17 24 39',
      brandMid: '17 24 39',
      brandEnd: '28 25 23',
      brandDivider: '147 197 253',
      formStart: '41 37 36',
      formMid: '28 25 23',
      formEnd: '28 25 23',
      formDivider: '147 197 253',
      formShadow: '0 0 0',
    },
    shadow: '0 0 0',
    overlay: {
      scrim: '0 0 0',
      brand: '214 211 209',
      accent: '147 197 253',
    },
    static: {
      black: '0 0 0',
      white: '255 255 255',
    },
  },
  shadow: {
    card: '0 12px 28px rgba(0, 0, 0, 0.28)',
    cockpitCard: '0 12px 24px rgba(0, 0, 0, 0.24)',
    elevated: '0 18px 44px rgba(0, 0, 0, 0.40)',
    dialog: '0 20px 56px rgba(0, 0, 0, 0.4)',
    focus: '0 0 0 4px rgba(214, 211, 209, 0.16)',
  },
  glow: {
    primary: '0 0 0 1px rgba(214, 211, 209, 0.16), 0 0 28px rgba(214, 211, 209, 0.08)',
    accent: '0 0 0 1px rgba(147, 197, 253, 0.16), 0 0 28px rgba(147, 197, 253, 0.08)',
  },
  ...shared,
}

export default createThemePreset({
  id: 'minimal',
  label: 'Minimal',
  modes: {
    light,
    dark,
  },
})
